import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

export interface ToastInput {
  message: ReactNode;
  tone?: 'info' | 'success' | 'error';
  /** ms; errors stay until dismissed (no timed loss of an error message). */
  duration?: number;
}
interface ToastItem extends ToastInput {
  id: number;
}
interface ToastApi {
  show: (t: ToastInput) => number;
  dismiss: (id: number) => void;
}

const Ctx = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const seq = useRef(0);
  const dismiss = useCallback((id: number) => setItems((xs) => xs.filter((t) => t.id !== id)), []);
  const show = useCallback(
    (t: ToastInput) => {
      const id = ++seq.current;
      setItems((xs) => [...xs, { ...t, id }]);
      const duration = t.duration ?? (t.tone === 'error' ? 0 : 6000);
      if (duration > 0) window.setTimeout(() => dismiss(id), duration);
      return id;
    },
    [dismiss],
  );
  const value = useMemo(() => ({ show, dismiss }), [show, dismiss]);
  const polite = items.filter((t) => t.tone !== 'error');
  const loud = items.filter((t) => t.tone === 'error');
  const render = (t: ToastItem) => (
    <div
      key={t.id}
      className="fl-toast"
      data-tone={t.tone ?? 'info'}
      role={t.tone === 'error' ? 'alert' : undefined}
    >
      <div className="flex-1 text-sm">{t.message}</div>
      <button
        type="button"
        className="fl-btn"
        data-variant="ghost"
        data-size="sm"
        onClick={() => dismiss(t.id)}
        aria-label="Dismiss notification"
      >
        ×
      </button>
    </div>
  );
  return (
    <Ctx.Provider value={value}>
      {children}
      <div className="fl-toasts">
        <div role="status" aria-live="polite" className="flex flex-col gap-2">
          {polite.map(render)}
        </div>
        <div className="flex flex-col gap-2">{loud.map(render)}</div>
      </div>
    </Ctx.Provider>
  );
}

export function useToast(): ToastApi {
  const v = useContext(Ctx);
  if (!v)
    throw new Error('useToast() must be used inside <ToastProvider>; wrap the app root with it.');
  return v;
}
